import React, { useState, useEffect, useRef, useCallback, ReactNode, JSX } from "react";
import { ChevronLeft, ChevronRight, ArrowLeft, CalendarDays, Heart, MessageCircle, X, Send } from "lucide-react";

/* =========================================================
     URL Web App Apps Script — data berita full diambil dari sini
     (Google Sheets), tidak ada data contoh/dummy sama sekali.
  ========================================================= */
const API_URL: string = "https://script.google.com/macros/s/AKfycbwnK9Z82DLqIOBlLUqaRva0V6lQ8wP9Q08k1GqsN2Vxe1pleEyM6R3bfFlRHaR3MgTC/exec";

// ===== Types =====
interface NewsItem {
  id: number;
  judul: string;
  author: string;
  gambar: string;
  deskripsi: string;
  kategori: string;
}

interface ApiListResponse {
  success: boolean;
  total?: number;
  data: NewsItem[];
  message?: string;
}

interface CommentItem {
  comment_id: string;
  id_berita: number | string;
  device_id: string;
  nama: string;
  komentar: string;
  timestamp: string;
}

// Info like + komentar per berita, disimpan di state utama (map by id)
interface Interaction {
  totalLike: number;
  sudahLike: boolean;
  totalComment: number;
}

function initials(name: string = ""): string {
  return name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

/* =========================================================
     DEVICE ID & NAMA TERSIMPAN
     (tanpa akun/login — identitas device disimpan di localStorage)
  ========================================================= */
const DEVICE_ID_KEY = "kopdes_device_id";
const SAVED_NAME_KEY = "kopdes_nama_komentar";

function getDeviceId(): string {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    // fallback kalau localStorage diblokir (mode privat, dll)
    return `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

function getSavedName(): string {
  try {
    return localStorage.getItem(SAVED_NAME_KEY) || "";
  } catch {
    return "";
  }
}

function saveName(nama: string): void {
  try {
    localStorage.setItem(SAVED_NAME_KEY, nama);
  } catch {
    /* noop */
  }
}

/* =========================================================
     HELPER FETCH KE APPS SCRIPT
     POST pakai FormData supaya cocok dengan e.parameter di Apps
     Script, dan supaya request masuk kategori "simple request"
     (menghindari CORS preflight yang tidak didukung Apps Script).
  ========================================================= */
async function apiGet(params: Record<string, string>): Promise<any> {
  const qs = new URLSearchParams(params).toString();
  const res = await fetch(`${API_URL}?${qs}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

async function apiPost(params: Record<string, string>): Promise<any> {
  const fd = new FormData();
  Object.entries(params).forEach(([k, v]) => fd.append(k, v));
  const res = await fetch(API_URL, { method: "POST", body: fd });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// ===== Stamp =====
interface StampProps {
  children: ReactNode;
  className?: string;
}

function Stamp({ children, className = "" }: StampProps) {
  return <span className={"inline-block -rotate-2 rounded-full border-[1.5px] border-dashed border-lime-600 bg-white/90 px-3 py-1 font-sora text-[10.5px] font-bold uppercase tracking-wider text-lime-700 " + className}>{children}</span>;
}

// ===== BackgroundBlobs =====
function BackgroundBlobs() {
  return (
    <>
      <div className="absolute -left-32 top-0 h-96 w-96 rounded-full bg-lime-200/40 blur-[140px]" />
      <div className="absolute right-0 top-40 h-80 w-80 rounded-full bg-green-100/40 blur-[130px]" />
      <div className="absolute bottom-0 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-lime-100/30 blur-[120px]" />
    </>
  );
}

/* =========================================================
     LIKE BUTTON (reusable — dipakai di card & detail)
  ========================================================= */
interface LikeButtonProps {
  liked: boolean;
  count: number;
  onToggle: () => void;
  size?: "sm" | "md";
}

function LikeButton({ liked, count, onToggle, size = "sm" }: LikeButtonProps) {
  const isSmall = size === "sm";
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      aria-pressed={liked}
      aria-label={liked ? "Batal suka" : "Suka berita ini"}
      className={
        "flex items-center gap-1.5 rounded-full border transition-all active:scale-95 " +
        (isSmall ? "px-2.5 py-1 text-[12.5px] " : "px-3.5 py-1.5 text-[13.5px] ") +
        (liked ? "border-red-200 bg-red-50 text-red-600" : "border-lime-600/20 bg-white text-lime-900/70 hover:bg-lime-50")
      }>
      <Heart size={isSmall ? 14 : 16} className={liked ? "fill-red-500 text-red-500" : ""} />
      <span className="font-semibold">{count}</span>
    </button>
  );
}

/* =========================================================
     COMMENT BUTTON (icon + jumlah — dipakai di card)
  ========================================================= */
interface CommentButtonProps {
  count: number;
  onClick: () => void;
  size?: "sm" | "md";
}

function CommentButton({ count, onClick, size = "sm" }: CommentButtonProps) {
  const isSmall = size === "sm";
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      aria-label="Lihat komentar"
      className={"flex items-center gap-1.5 rounded-full border border-lime-600/20 bg-white text-lime-900/70 transition-all hover:bg-lime-50 active:scale-95 " + (isSmall ? "px-2.5 py-1 text-[12.5px]" : "px-3.5 py-1.5 text-[13.5px]")}>
      <MessageCircle size={isSmall ? 14 : 16} />
      <span className="font-semibold">{count}</span>
    </button>
  );
}

/* =========================================================
     FORM KOMENTAR (dipakai di modal & di detail)
  ========================================================= */
interface CommentFormProps {
  onSubmit: (nama: string, teks: string) => Promise<void>;
  submitting: boolean;
}

function CommentForm({ onSubmit, submitting }: CommentFormProps) {
  const [nama, setNama] = useState<string>(getSavedName());
  const [teks, setTeks] = useState<string>("");

  const handleSubmit = async () => {
    if (!teks.trim()) return;
    await onSubmit(nama.trim() || "Anonim", teks.trim());
    setTeks("");
  };

  return (
    <div className="flex flex-col gap-2 border-t border-dashed border-lime-600/25 pt-4">
      <input
        value={nama}
        onChange={(e) => setNama(e.target.value)}
        placeholder="Nama kamu (opsional)"
        className="w-full rounded-xl border border-lime-600/20 bg-lime-50/50 px-3.5 py-2 text-[13.5px] text-lime-950 outline-none placeholder:text-lime-900/40 focus:border-lime-500"
      />
      <div className="flex items-end gap-2">
        <textarea
          value={teks}
          onChange={(e) => setTeks(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSubmit();
            }
          }}
          placeholder="Tulis komentar..."
          rows={2}
          className="flex-1 resize-none rounded-xl border border-lime-600/20 bg-lime-50/50 px-3.5 py-2 text-[13.5px] text-lime-950 outline-none placeholder:text-lime-900/40 focus:border-lime-500"
        />
        <button
          onClick={handleSubmit}
          disabled={submitting || !teks.trim()}
          aria-label="Kirim komentar"
          className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-lime-600 text-white transition hover:bg-lime-700 disabled:cursor-not-allowed disabled:opacity-40">
          <Send size={16} />
        </button>
      </div>
    </div>
  );
}

/* =========================================================
     LIST KOMENTAR (dipakai di modal & di detail)
  ========================================================= */
function CommentList({ comments, loading }: { comments: CommentItem[]; loading: boolean }) {
  if (loading) {
    return (
      <div className="flex flex-col gap-3 py-2">
        {[0, 1].map((i) => (
          <div key={i} className="flex animate-pulse gap-2.5">
            <div className="h-8 w-8 flex-shrink-0 rounded-full bg-lime-100" />
            <div className="flex-1 space-y-1.5">
              <div className="h-3 w-24 rounded-full bg-lime-100" />
              <div className="h-3 w-4/5 rounded-full bg-lime-100" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (comments.length === 0) {
    return <p className="py-4 text-center text-[13px] text-lime-900/45">Belum ada komentar. Jadi yang pertama, yuk!</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      {comments.map((c) => (
        <div key={c.comment_id} className="flex gap-2.5">
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-lime-200 text-[11px] font-bold text-lime-900">{initials(c.nama || "Anonim")}</span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-semibold text-lime-950">{c.nama || "Anonim"}</span>
              <span className="text-[11px] text-lime-900/40">{new Date(c.timestamp).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" })}</span>
            </div>
            <p className="mt-0.5 whitespace-pre-wrap break-words text-[13.5px] leading-relaxed text-lime-900/80">{c.komentar}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

/* =========================================================
     MODAL KOMENTAR (popup dari card)
  ========================================================= */
interface CommentModalProps {
  newsId: number;
  judul: string;
  onClose: () => void;
  onCommentAdded: (id: number) => void;
}

function CommentModal({ newsId, judul, onClose, onCommentAdded }: CommentModalProps) {
  const [comments, setComments] = useState<CommentItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [visible, setVisible] = useState<boolean>(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setVisible(true));
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    (async () => {
      try {
        setLoading(true);
        const json = await apiGet({ action: "getComments", id: String(newsId) });
        if (json.success) setComments(json.data);
      } catch (e) {
        console.error("Gagal ambil komentar:", e);
      } finally {
        setLoading(false);
      }
    })();

    return () => {
      cancelAnimationFrame(raf);
      document.body.style.overflow = prevOverflow;
    };
  }, [newsId]);

  const close = () => {
    setVisible(false);
    setTimeout(onClose, 200);
  };

  const handleSubmit = async (nama: string, teks: string) => {
    setSubmitting(true);
    try {
      saveName(nama);
      const json = await apiPost({
        action: "comment",
        id: String(newsId),
        device_id: getDeviceId(),
        nama,
        komentar: teks,
      });
      if (json.success) {
        setComments((prev) => [json.data, ...prev]);
        onCommentAdded(newsId);
      }
    } catch (e) {
      console.error("Gagal kirim komentar:", e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={"fixed inset-0 z-[999999999999] flex items-end justify-center bg-lime-950/40 transition-opacity duration-200 sm:items-center sm:p-4 " + (visible ? "opacity-100" : "opacity-0")} onClick={close}>
      <div
        onClick={(e) => e.stopPropagation()}
        className={"relative flex max-h-[85vh] w-full flex-col rounded-t-3xl bg-white shadow-2xl transition-transform duration-200 sm:max-w-lg sm:rounded-3xl " + (visible ? "translate-y-0" : "translate-y-6")}>
        <div className="flex items-start justify-between gap-3 border-b border-dashed border-lime-600/20 p-5">
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-wider text-lime-600">Komentar</p>
            <h3 className="mt-0.5 line-clamp-1 font-sora text-[15px] font-bold text-lime-950">{judul}</h3>
          </div>
          <button onClick={close} aria-label="Tutup" className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-lime-100 text-lime-700 hover:bg-lime-200">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          <CommentList comments={comments} loading={loading} />
        </div>

        <div className="p-5 pt-0 sm:pb-12 md:pb-5" style={{ paddingBottom: "max(2rem, env(safe-area-inset-bottom))" }}>
          <CommentForm onSubmit={handleSubmit} submitting={submitting} />
        </div>
      </div>
    </div>
  );
}

/* =========================================================
     COMMENT SECTION (versi inline — dipakai di DetailView)
  ========================================================= */
function CommentSection({ newsId, onCommentAdded }: { newsId: number; onCommentAdded: (id: number) => void }) {
  const [comments, setComments] = useState<CommentItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const json = await apiGet({ action: "getComments", id: String(newsId) });
        if (active && json.success) setComments(json.data);
      } catch (e) {
        console.error("Gagal ambil komentar:", e);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [newsId]);

  const handleSubmit = async (nama: string, teks: string) => {
    setSubmitting(true);
    try {
      saveName(nama);
      const json = await apiPost({
        action: "comment",
        id: String(newsId),
        device_id: getDeviceId(),
        nama,
        komentar: teks,
      });
      if (json.success) {
        setComments((prev) => [json.data, ...prev]);
        onCommentAdded(newsId);
      }
    } catch (e) {
      console.error("Gagal kirim komentar:", e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-10 max-w-2xl">
      <h4 className="mb-4 font-sora text-[15px] font-bold text-lime-950">Komentar ({comments.length})</h4>
      <CommentForm onSubmit={handleSubmit} submitting={submitting} />
      <div className="mt-5">
        <CommentList comments={comments} loading={loading} />
      </div>
    </div>
  );
}

// ===== Skeleton (loading state) =====
function CardSkeleton() {
  return (
    <div className="flex h-full animate-pulse flex-col overflow-hidden rounded-3xl border border-lime-600/10 bg-white shadow-[0_20px_40px_-20px_rgba(63,87,19,0.15)]">
      <div className="h-40 w-full bg-lime-100 sm:h-48" />
      <div className="flex flex-1 flex-col gap-3 p-5">
        <div className="h-4 w-4/5 rounded-full bg-lime-100" />
        <div className="h-4 w-3/5 rounded-full bg-lime-100" />
        <div className="h-3 w-full rounded-full bg-lime-100" />
        <div className="h-3 w-5/6 rounded-full bg-lime-100" />
        <div className="mt-auto flex items-center gap-2 border-t border-dashed border-lime-600/15 pt-3">
          <div className="h-6 w-6 rounded-full bg-lime-100" />
          <div className="h-3 w-24 rounded-full bg-lime-100" />
        </div>
      </div>
    </div>
  );
}

interface SkeletonSliderProps {
  perView: number;
}

function SkeletonSlider({ perView }: SkeletonSliderProps) {
  return (
    <div className="relative z-10">
      <div className="flex gap-5 overflow-hidden pb-2">
        {Array.from({ length: perView }).map((_, i) => (
          <div key={i} className="min-w-0 flex-1">
            <CardSkeleton />
          </div>
        ))}
      </div>
      <div className="mt-6 flex items-center justify-center gap-4">
        <div className="h-10 w-10 animate-pulse rounded-full bg-lime-100" />
        <div className="flex items-center gap-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-2 w-2 animate-pulse rounded-full bg-lime-100" />
          ))}
        </div>
        <div className="h-10 w-10 animate-pulse rounded-full bg-lime-100" />
      </div>
    </div>
  );
}

// ===== NewsCard =====
interface NewsCardProps {
  item: NewsItem;
  onOpen: (id: number) => void;
  interaction: Interaction;
  onToggleLike: (id: number) => void;
  onOpenComments: (id: number) => void;
}

function NewsCard({ item, onOpen, interaction, onToggleLike, onOpenComments }: NewsCardProps) {
  return (
    <div
      onClick={() => onOpen(item.id)}
      className="flex h-full cursor-pointer flex-col overflow-hidden rounded-3xl border border-lime-600/10 bg-white shadow-[0_20px_40px_-20px_rgba(63,87,19,0.25)] transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_28px_50px_-18px_rgba(63,87,19,0.32)]">
      <div className="group relative h-40 overflow-hidden sm:h-48">
        <img src={item.gambar} alt={item.judul} className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" />
        {/* <Stamp className="absolute left-3.5 top-3.5">{item.kategori}</Stamp> */}
      </div>
      <div className="flex flex-1 flex-col gap-2.5 p-5">
        <h3 className="font-sora text-[17px] font-bold leading-snug text-lime-950">{item.judul}</h3>
        <p className="line-clamp-2 text-[13.5px] leading-relaxed text-lime-800/70">{item.deskripsi}</p>

        <div className="mt-auto flex items-center gap-2 border-t border-dashed border-lime-600/25 pt-3 text-[12.5px] text-lime-900/70">
          <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-lime-200 text-[11px] font-bold text-lime-900">{initials(item.author)}</span>
          <span className="min-w-0 flex-1 truncate">{item.author}</span>
          <span>&middot;</span>
          <span className="flex-shrink-0">17 Jul 2026</span>
        </div>

        <div className="flex items-center gap-2 pt-1">
          <LikeButton liked={interaction.sudahLike} count={interaction.totalLike} onToggle={() => onToggleLike(item.id)} />
          <CommentButton count={interaction.totalComment} onClick={() => onOpenComments(item.id)} />
        </div>
      </div>
    </div>
  );
}

// ===== AutoSlider =====
interface AutoSliderProps {
  items: NewsItem[];
  onOpen: (id: number) => void;
  perView: number;
  interactions: Record<string, Interaction>;
  onToggleLike: (id: number) => void;
  onOpenComments: (id: number) => void;
}

const EMPTY_INTERACTION: Interaction = { totalLike: 0, sudahLike: false, totalComment: 0 };

function AutoSlider({ items, onOpen, perView, interactions, onToggleLike, onOpenComments }: AutoSliderProps) {
  const [index, setIndex] = useState<number>(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // reset index kalau perView berubah (misal resize dari mobile ke desktop)
  useEffect(() => {
    setIndex(0);
  }, [perView]);

  const maxIndex = Math.max(items.length - perView, 0);

  const next = useCallback(() => {
    setIndex((i) => (i >= maxIndex ? 0 : i + 1));
  }, [maxIndex]);

  const prev = () => setIndex((i) => (i <= 0 ? maxIndex : i - 1));

  useEffect(() => {
    timerRef.current = setInterval(next, 5000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [next]);

  const restartTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(next, 5000);
  };

  const slideWidth = 100 / perView;

  return (
    <div className="relative z-10">
      <div className="overflow-hidden pb-2">
        <div className="flex transition-transform duration-500 ease-out" style={{ transform: `translateX(-${index * slideWidth}%)` }}>
          {items.map((item) => (
            <div key={item.id} className="shrink-0 px-2.5" style={{ width: `${slideWidth}%` }}>
              <NewsCard item={item} onOpen={onOpen} interaction={interactions[String(item.id)] ?? EMPTY_INTERACTION} onToggleLike={onToggleLike} onOpenComments={onOpenComments} />
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 flex items-center justify-center gap-4">
        <button
          onClick={() => {
            prev();
            restartTimer();
          }}
          aria-label="Sebelumnya"
          className="flex h-10 w-10 items-center justify-center rounded-full border border-lime-600/30 bg-white text-lime-700 shadow-sm transition hover:bg-lime-100">
          <ChevronLeft size={18} />
        </button>

        <div className="flex items-center gap-2">
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <button
              key={i}
              onClick={() => {
                setIndex(i);
                restartTimer();
              }}
              aria-label={`Slide ${i + 1}`}
              className={"h-2 rounded-full transition-all " + (i === index ? "w-6 bg-lime-600" : "w-2 bg-lime-600/30")}
            />
          ))}
        </div>

        <button
          onClick={() => {
            next();
            restartTimer();
          }}
          aria-label="Berikutnya"
          className="flex h-10 w-10 items-center justify-center rounded-full border border-lime-600/30 bg-white text-lime-700 shadow-sm transition hover:bg-lime-100">
          <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
}

// ===== ListView =====
interface ListViewProps {
  items: NewsItem[];
  onOpen: (id: number) => void;
  loading: boolean;
  interactions: Record<string, Interaction>;
  onToggleLike: (id: number) => void;
  onOpenComments: (id: number) => void;
}

function useResponsivePerView(): number {
  const [perView, setPerView] = useState<number>(1);
  useEffect(() => {
    const calc = () => {
      const w = window.innerWidth;
      if (w >= 1200) setPerView(3);
      else if (w >= 900) setPerView(2);
      else setPerView(1);
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, []);
  return perView;
}

function ListView({ items, onOpen, loading, interactions, onToggleLike, onOpenComments }: ListViewProps) {
  const perView = useResponsivePerView();

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-lime-50 via-white to-white py-10 sm:py-14 md:py-20">
      <BackgroundBlobs />

      <div className="relative z-10 mx-auto max-w-5xl px-4 sm:px-6 lg:px-5 ">
        <div className="">
          <h1 className="mt-4 text-center  font-sora text-[26px] font-extrabold leading-tight  sm:text-[32px] md:text-[44px] text-red-700">
            Hot Issue <br />
            <span className="text-lime-900">Kopdes Merah Putih</span>
          </h1>
        </div>

        <p className="mt-3  text-[14px] leading-relaxed text-lime-900/60 sm:text-[15px] md:text-base text-center">Kumpulan isu seputar pembangunan, pelatihan manajer, hingga kebijakan dana desa untuk Koperasi Desa/Kelurahan Merah Putih.</p>

        <div className="mt-8 sm:mt-10">
          {loading ? (
            <SkeletonSlider perView={perView} />
          ) : items.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-lime-600/30 bg-lime-50/60 py-16 text-center text-sm text-lime-900/50">Belum ada berita untuk ditampilkan.</div>
          ) : (
            <AutoSlider items={items} onOpen={onOpen} perView={perView} interactions={interactions} onToggleLike={onToggleLike} onOpenComments={onOpenComments} />
          )}
        </div>
      </div>
    </section>
  );
}

// ===== DetailView =====
interface DetailViewProps {
  item: NewsItem | null;
  onBack: () => void;
  interaction: Interaction;
  onToggleLike: (id: number) => void;
  onCommentAdded: (id: number) => void;
}

// ====== Parser deskripsi jadi heading + paragraf ======
function renderDeskripsi(teks: string) {
  const baris = teks
    .split("\n")
    .map((b) => b.trim())
    .filter(Boolean);

  return baris.map((b, i) => {
    // deteksi heading: baris pendek & gak diakhiri tanda baca kalimat
    const isHeading = b.length < 60 && !/[.,]$/.test(b);

    if (isHeading) {
      return (
        <h3 key={i} className="mt-6 mb-2 max-w-2xl font-sora text-[17px] font-bold text-lime-950 md:text-[19px]">
          {b}
        </h3>
      );
    }

    return (
      <p key={i} className="mb-4 max-w-2xl text-[16px] leading-[1.85] text-lime-950/80 text-justify text-pretty hyphens-auto">
        {b}
      </p>
    );
  });
}

function DetailView({ item, onBack, interaction, onToggleLike, onCommentAdded }: DetailViewProps) {
  const [visible, setVisible] = useState<boolean>(false);
  const closedRef = useRef(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setVisible(true));

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    window.history.pushState({ detailOverlay: true }, "");

    const handlePopState = () => {
      closeWithAnimation();
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      cancelAnimationFrame(raf);
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("popstate", handlePopState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeWithAnimation = () => {
    if (closedRef.current) return;
    closedRef.current = true;

    setVisible(false);
    setTimeout(onBack, 250);
  };

  const handleBack = () => {
    window.history.back();
  };

  if (!item) return null;

  return (
    <div className={"fixed inset-0 z-50 overflow-y-auto bg-white transition-opacity duration-300 " + (visible ? "opacity-100" : "opacity-0")}>
      <section className={"relative overflow-hidden bg-gradient-to-b from-lime-50 via-white to-white py-10 transition-transform duration-300 ease-out sm:py-14 md:py-20 " + (visible ? "translate-y-0" : "translate-y-4")}>
        <BackgroundBlobs />

        <div className="relative z-[9999999] mx-auto max-w-3xl px-4 sm:px-6 lg:px-5 pb-12">
          <button onClick={handleBack} className="mb-7 inline-flex items-center gap-2 rounded-full border border-lime-200 bg-lime-100 px-4 py-2 text-[13.5px] font-semibold text-lime-900/70 transition hover:bg-lime-200">
            <ArrowLeft size={15} /> Kembali
          </button>

          <img src={item.gambar} alt={item.judul} className="mb-7 h-56 w-full rounded-3xl object-cover shadow-[0_24px_50px_-22px_rgba(63,87,19,0.35)] md:h-[340px]" />

          <h1 className="mb-4 max-w-2xl font-sora text-[24px] font-extrabold leading-tight text-lime-950 md:text-[36px]">{item.judul}</h1>

          <div className="mb-5 flex flex-wrap items-center gap-2.5 text-[13.5px] text-lime-900/60">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-lime-200 text-[12px] font-bold text-lime-900">{initials(item.author)}</span>
            <span>{item.author}</span>
            <span>&middot;</span>
            <span className="flex items-center gap-1">
              <CalendarDays size={13} /> 17 Juli 2026
            </span>
          </div>

          <div className="mb-7 flex items-center gap-2">
            <LikeButton liked={interaction.sudahLike} count={interaction.totalLike} onToggle={() => onToggleLike(item.id)} size="md" />
          </div>

          {/* ganti <p> tunggal jadi hasil parsing */}
          <div>{renderDeskripsi(item.deskripsi)}</div>

          <CommentSection newsId={item.id} onCommentAdded={onCommentAdded} />
        </div>
      </section>
    </div>
  );
}

// ===== Main component =====
export default function KabarKopdes(): JSX.Element {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [interactions, setInteractions] = useState<Record<string, Interaction>>({});
  const [commentModalId, setCommentModalId] = useState<number | null>(null);

  // hydrate like + jumlah komentar untuk semua berita setelah list terambil
  const hydrateInteractions = useCallback(async (items: NewsItem[]) => {
    const deviceId = getDeviceId();
    const results = await Promise.all(
      items.map(async (item) => {
        try {
          const [likeJson, commentJson] = await Promise.all([apiGet({ action: "getLikes", id: String(item.id), device_id: deviceId }), apiGet({ action: "getComments", id: String(item.id) })]);
          return {
            id: String(item.id),
            interaction: {
              totalLike: likeJson.success ? likeJson.total_like : 0,
              sudahLike: likeJson.success ? likeJson.sudah_like : false,
              totalComment: commentJson.success ? commentJson.total ?? commentJson.data.length : 0,
            } as Interaction,
          };
        } catch (e) {
          console.error("Gagal ambil interaksi untuk id", item.id, e);
          return { id: String(item.id), interaction: EMPTY_INTERACTION };
        }
      })
    );

    setInteractions((prev) => {
      const next = { ...prev };
      results.forEach((r) => (next[r.id] = r.interaction));
      return next;
    });
  }, []);

  useEffect(() => {
    if (!API_URL) {
      setError("API_URL belum diisi.");
      setLoading(false);
      return;
    }

    const fetchNews = async (): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_URL}?action=list`);
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const json: ApiListResponse = await res.json();
        if (json.success) {
          setNews(json.data);
          hydrateInteractions(json.data);
        } else {
          throw new Error(json.message || "Response API tidak sukses");
        }
      } catch (e) {
        console.error("Gagal ambil data dari API_URL:", e);
        setError(e instanceof Error ? e.message : "Gagal fetch data dari Apps Script");
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [hydrateInteractions]);

  // toggle like — optimistic update dulu, lalu disesuaikan dengan response API
  const handleToggleLike = useCallback(
    async (id: number) => {
      const key = String(id);
      const prevInteraction = interactions[key] ?? EMPTY_INTERACTION;

      setInteractions((prev) => ({
        ...prev,
        [key]: {
          ...prevInteraction,
          sudahLike: !prevInteraction.sudahLike,
          totalLike: prevInteraction.sudahLike ? prevInteraction.totalLike - 1 : prevInteraction.totalLike + 1,
        },
      }));

      try {
        const json = await apiPost({ action: "like", id: key, device_id: getDeviceId() });
        if (json.success) {
          setInteractions((prev) => ({
            ...prev,
            [key]: { ...(prev[key] ?? EMPTY_INTERACTION), sudahLike: json.liked, totalLike: json.total_like },
          }));
        } else {
          // rollback kalau gagal
          setInteractions((prev) => ({ ...prev, [key]: prevInteraction }));
        }
      } catch (e) {
        console.error("Gagal toggle like:", e);
        setInteractions((prev) => ({ ...prev, [key]: prevInteraction }));
      }
    },
    [interactions]
  );

  // dipanggil setelah komentar baru berhasil ditambahkan (dari modal maupun detail)
  const handleCommentAdded = useCallback((id: number) => {
    const key = String(id);
    setInteractions((prev) => ({
      ...prev,
      [key]: { ...(prev[key] ?? EMPTY_INTERACTION), totalComment: (prev[key]?.totalComment ?? 0) + 1 },
    }));
  }, []);

  const activeItem: NewsItem | null = news.find((n) => n.id === activeId) ?? null;
  const modalItem: NewsItem | null = news.find((n) => n.id === commentModalId) ?? null;

  return (
    <div className="min-h-screen w-full font-['Inter',sans-serif]">
      <style>{`
          .font-sora { font-family: 'Sora', sans-serif; }
        `}</style>
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=Inter:wght@400;500;600;700&display=swap" />

      {activeItem ? (
        <DetailView item={activeItem} onBack={() => setActiveId(null)} interaction={interactions[String(activeItem.id)] ?? EMPTY_INTERACTION} onToggleLike={handleToggleLike} onCommentAdded={handleCommentAdded} />
      ) : (
        <ListView items={news} onOpen={setActiveId} loading={loading} interactions={interactions} onToggleLike={handleToggleLike} onOpenComments={setCommentModalId} />
      )}

      {modalItem && <CommentModal newsId={modalItem.id} judul={modalItem.judul} onClose={() => setCommentModalId(null)} onCommentAdded={handleCommentAdded} />}
    </div>
  );
}
